import { useMemo, useState } from "react";
import { AlertCircle, Check } from "lucide-react";
import { useUser } from "../../../context/UserContext";
import { useEleveWitouthGroup } from "../../../hooks/fetch_api_hook/useEleveWithoutGroup";
import { usePostApi } from "../../../hooks/post_api_hook/usePostApi";
import { url } from "../../../utils/confserveur";
import Loading from "../../general/Loading";

const AUCUNE_CLASSE = [];
const AUCUN_ELEVE = [];

// Ordre de la liste d'appel : nom, puis prénom, sans tenir compte des accents.
const parNomPuisPrenom = (a, b) =>
  (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" }) ||
  (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" });

const libelleCompteur = (n) => (n > 1 ? `${n} élèves cochés` : `${n} élève coché`);

/**
 * Composer un DEMI-GROUPE encore vide (décision prof 2026-09-30) : le nom du
 * groupe en grand, tous les élèves de sa classe à cocher, un compteur et
 * « Enregistrer le demi-groupe ». Le prof coche lui-même : un élève peut être
 * dans plusieurs demi-groupes (et dans son groupe de besoin). La liste vient du
 * back (groupes/eleves-sans-groupe/ avec groupe_id) : les élèves de la classe
 * qui ne sont pas encore dans CE demi-groupe.
 */
function ComposerDemiGroupe({ groupe, groupeId, groupNom, handleReload, handleMessagePost }) {
  const { etablissementData, user } = useUser();
  const classes = groupe?.classes_detail ?? AUCUNE_CLASSE;
  const classe = classes[0];

  const { data, isLoading, isError } = useEleveWitouthGroup(
    etablissementData?.id,
    classes,
    user?.matiere,
    etablissementData?.id ? true : "not-enabled",
    groupeId,
  );
  const [{ isLoading: enregistrement }, doPost] = usePostApi(url + "groupes/affecter-eleves/");

  const [coches, setCoches] = useState(() => new Set());
  const [echec, setEchec] = useState(false);

  const candidats = data?.eleves_sans_groupe;
  const eleves = useMemo(() => [...(candidats ?? AUCUN_ELEVE)].sort(parNomPuisPrenom), [candidats]);

  const basculer = (id) => {
    setCoches((avant) => {
      const apres = new Set(avant);
      if (apres.has(id)) apres.delete(id);
      else apres.add(id);
      return apres;
    });
  };

  const enregistrer = async () => {
    setEchec(false);
    const reponse = await doPost({ affectations: [{ groupId: groupeId, elevesIds: [...coches] }] });
    if (!reponse.success) {
      setEchec(true);
      return;
    }
    handleMessagePost(
      coches.size > 1
        ? `${coches.size} élèves ajoutés au demi-groupe ${groupNom}`
        : `1 élève ajouté au demi-groupe ${groupNom}`,
    );
    // Le groupe rechargé n'est plus vide : la page Élèves habituelle prend le relais.
    handleReload();
  };

  let contenu;
  if (isError) {
    contenu = (
      <p role="alert" className="flex items-center gap-2 text-sm font-semibold text-red-700">
        <AlertCircle size={16} className="shrink-0" />
        Les élèves de la classe n'ont pas pu être chargés. Rechargez la page.
      </p>
    );
  } else if (isLoading || !Array.isArray(candidats)) {
    contenu = <Loading loadingText="Chargement des élèves de la classe..." />;
  } else if (eleves.length === 0) {
    contenu = (
      <p className="text-sm text-slate-600">
        Aucun élève trouvé dans la classe{classe ? ` ${classe}` : ""}. Ses élèves arrivent par l'
        <span className="font-bold">Import établissement</span> (délégué d'affectation ou chef d'établissement).
      </p>
    );
  } else {
    contenu = (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {eleves.map((eleve) => {
          const coche = coches.has(eleve.id);
          return (
            <label
              key={eleve.id}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                coche ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
            >
              <input
                type="checkbox"
                checked={coche}
                onChange={() => basculer(eleve.id)}
                disabled={enregistrement}
                className="h-4 w-4 shrink-0 accent-blue-700"
              />
              <span className="text-sm font-medium text-slate-800">
                {eleve.nom} {eleve.prenom}
              </span>
            </label>
          );
        })}
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-lg shadow-blue-900/5 border border-slate-100">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-cyan-600">Demi-groupe à composer</p>
          <h1 className="mt-1 text-3xl font-extrabold text-blue-900">{groupNom}</h1>
          <p className="mt-1 text-sm text-slate-600">
            {classe ? `Cochez les élèves de ${classe} qui en font partie.` : "Cochez les élèves qui en font partie."}
          </p>
        </div>

        <div className="p-6">{contenu}</div>

        <div className="sticky bottom-0 px-6 py-4 border-t border-slate-100 bg-white rounded-b-xl flex flex-wrap items-center justify-between gap-3">
          <p aria-live="polite" className="text-sm font-semibold text-slate-700">
            {libelleCompteur(coches.size)}
          </p>
          {echec ? (
            <p role="alert" className="text-sm font-semibold text-red-700">
              L'enregistrement n'a pas abouti. Réessayez.
            </p>
          ) : null}
          <button
            type="button"
            onClick={enregistrer}
            disabled={coches.size === 0 || enregistrement}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-sm font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check size={16} />
            {enregistrement ? "Enregistrement..." : "Enregistrer le demi-groupe"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ComposerDemiGroupe;
